import {
  useCallback,
  useEffect,
  useRef,
  useState,
  ChangeEvent,
  FC
} from 'react';

import { InputsDivider, InputsWrapper, SizeInput } from './Wrapper';

type Direction = 'width' | 'height';

export type Sizes = {
  [key in Direction]: number;
};

type SizeInputsProps = {
  sizes: Sizes;
  onChange: (sizes: Sizes) => void;
  disabled?: boolean;
  maxLength?: number;
  keepProportion?: boolean;
};

const SizeInputs: FC<SizeInputsProps> = ({
  disabled,
  onChange,
  maxLength,
  sizes: initialSizes,
  keepProportion = false
}) => {
  const [sizes, setSizes] = useState<Sizes>(initialSizes);
  const ratio = useRef(initialSizes.width / initialSizes.height).current;

  const getOppositeValue = (newValue: number, direction: Direction) => {
    const oppositeValue = Object.keys(sizes).filter(
      d => d === direction
    )[0] as Direction;

    if (!keepProportion) return sizes[oppositeValue];

    return direction === 'width'
      ? Math.round(ratio * newValue)
      : Math.round(newValue / ratio);
  };

  const handleSizeChange = useCallback(
    ({ target: { value, id } }: ChangeEvent<HTMLInputElement>) => {
      const newValue = parseInt(value, 10) || 0;

      setSizes({
        width: id === 'width' ? newValue : getOppositeValue(newValue, 'width'),
        height:
          id === 'height' ? newValue : getOppositeValue(newValue, 'height')
      });
    },
    [sizes]
  );

  useEffect(() => {
    onChange(sizes);
  }, [sizes.width, sizes.height]);

  return (
    <InputsWrapper>
      <SizeInput
        id="width"
        value={sizes.width}
        maxLength={maxLength || 4}
        onChange={handleSizeChange}
        {...(disabled && { disabled })}
      />
      <InputsDivider>&#10005;</InputsDivider>
      <SizeInput
        id="height"
        value={sizes.height}
        maxLength={maxLength || 4}
        onChange={handleSizeChange}
        {...(disabled && { disabled })}
      />
    </InputsWrapper>
  );
};

export default SizeInputs;
